/* global Word */
import type { TemplateProfile } from '../templates';
import type { ElementDefinition } from './types';

const BOX_W_PT = 390;
const H_PAD_PT = 14;
const V_PAD_TOP = 18;
const V_PAD_BOT = 10;

function toHex(color: string): string {
  return color.startsWith('#') ? color : `#${color}`;
}

function clearTableBorders(table: Word.Table): void {
  const tableBorders: Word.BorderLocation[] = [
    Word.BorderLocation.top,
    Word.BorderLocation.bottom,
    Word.BorderLocation.left,
    Word.BorderLocation.right,
    Word.BorderLocation.insideHorizontal,
    Word.BorderLocation.insideVertical,
  ];
  tableBorders.forEach((loc) => {
    table.getBorder(loc).type = Word.BorderType.none;
  });
}

function clearCellBorders(cell: Word.TableCell): void {
  const cellBorders: Word.BorderLocation[] = [
    Word.BorderLocation.top,
    Word.BorderLocation.bottom,
    Word.BorderLocation.left,
    Word.BorderLocation.right,
  ];
  cellBorders.forEach((loc) => {
    cell.getBorder(loc).type = Word.BorderType.none;
  });
}

/** Insert executive summary shaded box. */
export async function insertExecutiveSummaryElement(profile: TemplateProfile): Promise<void> {
  await Word.run(async (ctx) => {
    const anchorPara = ctx.document.getSelection().paragraphs.getFirst();
    const table = anchorPara.insertTable(1, 1, Word.InsertLocation.before, [['']]);
    clearTableBorders(table);

    await ctx.sync();

    const cell = table.getCell(0, 0);
    cell.columnWidth = BOX_W_PT;
    cell.shadingColor = '#D9D9D9';
    clearCellBorders(cell);

    const contentPara = cell.body.paragraphs.getFirst();
    contentPara.alignment = Word.Alignment.justified;
    contentPara.spaceBefore = V_PAD_TOP;
    contentPara.spaceAfter = V_PAD_BOT;
    contentPara.leftIndent = H_PAD_PT;
    contentPara.rightIndent = H_PAD_PT;
    contentPara.lineSpacing = 13.8;
    contentPara.font.name = profile.typography.fontFamily;
    contentPara.font.size = profile.typography.graphHintSize;
    contentPara.font.bold = false;
    contentPara.font.color = toHex(profile.colorPalette.sourceText);

    await ctx.sync();
  });
}

/** Element definition for executive summary insertion. */
export const executiveSummaryElement: ElementDefinition = {
  id: 'executive-summary',
  label: 'Exec Summary',
  icon: 'TextboxRegular',
  description: 'Shaded text box for summary content',
  category: 'text',
  insertAsync: insertExecutiveSummaryElement,
};
